import { Suspense, startTransition, useEffect, useState } from 'react'
import { MdEdit } from 'react-icons/md'
import { tagConvert } from 'utils/tagConvert'
import {
  CompanyType,
  ResponseCompany,
  useAllCompany
} from 'api/company/useAllCompany'
import { updateFocusCustomer } from 'api/company/updateFocusCustomer'
import { dateToString } from 'utils/dateToString'
import { useNavigate } from 'react-router-dom'
import { SearchInput } from 'components/atoms/inputs/SearchInput'
import { LoadingOverlay } from 'components/atoms/overlay/LoadingOverlay'
import { MemoModal } from 'components/modals/MemoModal'
import { YellowButton } from 'components/atoms/buttons/YellowButton'

const CompanyDetailTable = (props: {
  searchText: string
  company: ResponseCompany['data']
  onUpdate: () => void
}) => {
  const { company, searchText, onUpdate } = props
  const navigate = useNavigate()
  const [displayData, setDisplayData] = useState<CompanyType[]>([])
  const [selectCompanyId, setSelectCompanyId] = useState<number>()

  useEffect(() => {
    if (searchText !== '') {
      const filterData = company.filter((row) => {
        const { sales, ...params } = row
        const valueArray = Object.values(params)
          .filter((value) => !(value === null || typeof value === 'boolean'))
          .map((value) => String(value))
          .filter((word) => word.indexOf(searchText) !== -1)

        const salesSearch = sales.filter((sales) => {
          const valueArray = Object.values(sales)
            .filter((value) => !(value === null || typeof value === 'boolean'))
            .map((value) => String(value))
            .filter((word) => word.indexOf(searchText) !== -1)
          return valueArray.length > 0
        })

        return valueArray.length > 0 || salesSearch.length > 0
      })
      setDisplayData(filterData)
    } else {
      setDisplayData(company)
    }
  }, [company, searchText])

  const getAlertStyle = (row: CompanyType): React.CSSProperties | undefined => {
    const data = row as any
    const updateDate = new Date(row.updateAt)
    const limitPeriod =
      data.frequency === 'HIGH'
        ? 1
        : data.frequency === 'MIDDLE'
          ? 3
          : data.frequency === 'LOW'
            ? 6
            : null
    if (limitPeriod === null) return undefined
    const today = new Date()
    updateDate.setMonth(updateDate.getMonth() + limitPeriod)
    return updateDate < today ? { backgroundColor: '#E79999' } : undefined
  }

  return (
    <>
      <div className="max-h-[600px] overflow-auto">
        <table className="w-full border-collapse whitespace-nowrap text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b-2 border-[#ddd] bg-[#f8f9fa]">
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#555]">
                会社名
              </th>
              <th className="w-[100px] px-4 py-3 text-left text-sm font-semibold text-[#555]">
                注力顧客
              </th>
              <th className="w-[100px] px-4 py-3 text-left text-sm font-semibold text-[#555]">
                打ち合せ
              </th>
              <th className="w-[130px] px-4 py-3 text-left text-sm font-semibold text-[#555]">
                発起
              </th>
              <th className="w-[100px] px-4 py-3 text-left text-sm font-semibold text-[#555]">
                更新回数
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#555]">
                配信タグ
              </th>
              {[1, 2, 3, 4, 5].map((n) => (
                <th
                  key={n}
                  className="px-4 py-3 text-left text-sm font-semibold text-[#555]"
                >
                  営業 {n}
                </th>
              ))}
              <th className="w-[80px] px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {displayData.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer border-b border-[#eee] hover:bg-[#f8f9fa]"
                onClick={() => navigate('/company/' + row.id)}
              >
                <td className="px-4 py-3">{row.name}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={async () => {
                      await updateFocusCustomer(row.id, !row.focusCustomer)
                      onUpdate()
                    }}
                    className="flex flex-shrink-0 items-center gap-1.5 px-2 py-1 text-xs font-medium text-[#555] transition-colors"
                  >
                    <span
                      className={[
                        'relative inline-flex h-5 w-10 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200',
                        row.focusCustomer ? 'bg-[#01695C]' : 'bg-gray-300'
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200',
                          row.focusCustomer ? 'translate-x-5' : 'translate-x-0',
                          !row.focusCustomer && 'bg-gray-500'
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      />
                    </span>
                  </button>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <YellowButton
                    label={'メモ'}
                    clickHandler={() =>
                      startTransition(() => setSelectCompanyId(row.id))
                    }
                  />
                </td>
                <td className="px-4 py-3" style={getAlertStyle(row)}>
                  {dateToString(row.updateAt)}
                </td>
                <td className="px-4 py-3">{row.counter}</td>
                <td className="px-4 py-3">{tagConvert(row.tag)}</td>
                {[0, 1, 2, 3, 4].map((i) => (
                  <td key={i} className="px-4 py-3">
                    {row.sales[i]
                      ? `${row.sales[i].lastName} ${row.sales[i].firstName}`
                      : ''}
                  </td>
                ))}
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => navigate('/company/' + row.id + '/edit')}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-[#01695C]"
                  >
                    <MdEdit size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selectCompanyId && (
        <MemoModal
          companyId={selectCompanyId}
          onClose={() => setSelectCompanyId(undefined)}
          onUpdate={onUpdate}
        />
      )}
    </>
  )
}
export const CompanyList = (): JSX.Element => {
  const { data, mutate } = useAllCompany()
  const [searchText, setSearchText] = useState<string>('')

  return (
    <>
      <div className={'mb-5 flex items-center gap-3'}>
        <SearchInput searchText={searchText} setHandler={setSearchText} />
      </div>
      <Suspense fallback={<LoadingOverlay />}>
        {data && (
          <CompanyDetailTable
            company={data}
            searchText={searchText}
            onUpdate={() => mutate()}
          />
        )}
      </Suspense>
    </>
  )
}
